import { Component } from '@theme/component';

/**
 * Reproduces the sliding-pill selection animation that assets/variant-picker.js drives for its
 * "buttons" style — reusing the exact same CSS (snippets/variant-picker-styles.liquid) and markup
 * shape, but as its own standalone component. Needed for fieldsets that are NOT real product
 * variant options (eg. blocks/custom-option.liquid's Shape/Finish pills, which post as line-item
 * properties) and therefore must never live inside <variant-picker> — see the doc comment in
 * sections/product-design-editor.liquid explaining why those inputs can't share that element
 * without misfiring variant-picker.js's re-fetch logic.
 *
 * @extends {Component}
 */
export default class PillFieldset extends Component {
  /** @type {number[]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[]} */
  #radios = [];

  connectedCallback() {
    super.connectedCallback();
    this.#radios = Array.from(this.querySelectorAll('input'));

    const initialCheckedIndex = this.#radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
    if (initialCheckedIndex !== -1) {
      this.#checkedIndices = [initialCheckedIndex];
    }

    this.addEventListener('change', this.#handleChange.bind(this));
  }

  /**
   * @param {Event} event
   */
  #handleChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    const inputIndex = this.#radios.indexOf(target);
    if (inputIndex === -1) return;

    const [currentIndex, previousIndex] = this.#checkedIndices;

    // A real selection change always moves TO a different index than the one already current —
    // a change event for the index that's already current isn't a real transition (nothing this
    // component cares about actually changed), just a redundant re-check. Most commonly fired by
    // sections/product-design-editor.liquid's #applyCarriedOverProperties, which force-sets
    // .checked and dispatches change for a value carried over from the previous page's URL even
    // when it already matches this field's own default — otherwise harmless, except the unshift
    // below doesn't expect its own current index to reappear: it would push it to the "previous"
    // slot and immediately flip ITS OWN data-current-checked back to 'false', leaving no radio in
    // this group marked current at all (both then read as "not selected").
    if (inputIndex === currentIndex) return;

    if (currentIndex !== undefined && this.#radios[currentIndex]) {
      this.#radios[currentIndex].dataset.previousChecked = 'false';
    }
    if (previousIndex !== undefined && this.#radios[previousIndex]) {
      this.#radios[previousIndex].dataset.previousChecked = 'false';
    }

    this.#checkedIndices.unshift(inputIndex);
    this.#checkedIndices.length = Math.min(this.#checkedIndices.length, 2);

    const [newCurrentIndex, newPreviousIndex] = this.#checkedIndices;

    if (newCurrentIndex !== undefined && this.#radios[newCurrentIndex]) {
      this.#radios[newCurrentIndex].dataset.currentChecked = 'true';
    }
    if (newPreviousIndex !== undefined && this.#radios[newPreviousIndex]) {
      this.#radios[newPreviousIndex].dataset.previousChecked = 'true';
      this.#radios[newPreviousIndex].dataset.currentChecked = 'false';
    }

    this.#updateCss();
  }

  #updateCss() {
    const [currentIndex, previousIndex] = this.#checkedIndices;
    const currentWidth = currentIndex !== undefined ? this.#radios[currentIndex]?.parentElement?.offsetWidth : undefined;
    const previousWidth = previousIndex !== undefined ? this.#radios[previousIndex]?.parentElement?.offsetWidth : undefined;

    if (currentWidth) {
      this.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      this.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      this.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      this.style.removeProperty('--pill-width-previous');
    }
  }
}

if (!customElements.get('pill-fieldset-component')) {
  customElements.define('pill-fieldset-component', PillFieldset);
}
